import { z } from 'zod';

/**
 * NEX-1169 (plan 2026-10-20, Tarea 1.10) — the depósito's bank account, where Nexvia pays what it
 * collects for its guardados. Ground truth read directly off `nexvia-api-web` (2026-10-20):
 *
 * - `GET /storage/me/bank-account` — `get-storage-bank-account.handler.ts`: BARE (the controller's
 *   `res.status(200).json(result)`), `null` while the depósito has saved none, otherwise
 *   `storage-bank-account.repository.ts`'s `findByStorageProfileId` row: camelCase keys, `bankName`
 *   joined from `bank_catalog`.
 * - `PATCH /storage/me/bank-account` — `upsert-storage-bank-account.handler.ts` over
 *   `storage.schemas.ts`'s `upsertStorageBankAccountBody` (eight optional strings): BARE, the same
 *   row through the repository's `toPlain`, whose `bankName` is always `null` (no join on save).
 *   Refusals: 400 `STORAGE_BANK_CODE_INVALID` (not an active `bank_catalog` code), 400
 *   `STORAGE_BANK_ACCOUNT_NUMBER_INVALID` (the per-bank format rule; its `message` is already the
 *   Spanish rule, e.g. "BROU: 10 dígitos (viejo formato) o 14 dígitos (nuevo formato)"), 404
 *   `RESOURCE_NOT_FOUND` (no storage profile), 400 `VALIDATION_FIELD_INVALID` (a field that is not a
 *   string).
 * - `storage-bank-account.entity.ts` — `beneficiary_name` varchar(100), `document_type` 20,
 *   `document_number` 50, `bank_code` 10, `bank_account_type` 20, `account_number` 50, `bank_email`
 *   100 nullable, `payment_method` 20 default `bank_transfer`, `is_verified` default false.
 *
 * TOLERANT on purpose (plan rule: frontends read new contracts with `.nullable().optional()`):
 * every field but `id` may be missing or `null`, so a column added, dropped or left empty on the API
 * side degrades to "—" on screen instead of a `CONTRACT_VIOLATION`. Unknown keys are stripped.
 */
export const StorageBankAccountSchema = z.object({
  id: z.number(),
  storageProfileId: z.number().nullable().optional(),
  paymentMethod: z.string().nullable().optional(),
  beneficiaryName: z.string().nullable().optional(),
  documentType: z.string().nullable().optional(),
  documentNumber: z.string().nullable().optional(),
  bankCode: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  bankAccountType: z.string().nullable().optional(),
  accountNumber: z.string().nullable().optional(),
  bankEmail: z.string().nullable().optional(),
  isVerified: z.boolean().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional()
});
export type StorageBankAccount = z.infer<typeof StorageBankAccountSchema>;

/**
 * `GET /storage/me/bank-account`'s answer: the account, or `null` when there is none. An EMPTY
 * OBJECT also reads as "none": `src/shared/api.ts` folds a JSON `null` body into `{}` (`raw.payload
 * ?? {}`), so the shared transport's call to this endpoint never sees the `null` itself.
 */
export const StorageBankAccountResponseSchema = z.preprocess(
  (value) =>
    value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0 ? null : value,
  StorageBankAccountSchema.nullable()
);

/**
 * One `bank_catalog` row as `GET /driver/bank-account/options` sends it — the plain TypeORM entity
 * (`driver/entities/bank-catalog.entity.ts`, snake_case keys, `findByCountry`: active rows of that
 * country, by name). It is the same catalog `upsert-storage-bank-account.handler.ts` checks the
 * code against (`bankCatalogRepository.findActiveByCode`), so a bank picked from it passes
 * `STORAGE_BANK_CODE_INVALID`. `account_number_hint` is the backoffice's own hint for the format.
 */
export const BankCatalogEntrySchema = z.object({
  id: z.number().optional(),
  code: z.string(),
  name: z.string(),
  country_code: z.string().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  account_number_regex: z.string().nullable().optional(),
  account_number_hint: z.string().nullable().optional()
});
export type BankCatalogEntry = z.infer<typeof BankCatalogEntrySchema>;

/**
 * `GET /driver/bank-account/options?country=UY` — `driver/handlers/get-bank-account-options.handler.ts`,
 * bare `{ banks, paymentMethods, documentTypes, accountTypes, departments }`. The route has only
 * `authMiddleware` (`driver.routes.ts`), no carrier check, so a depósito's login may call it; there
 * is no storage-specific catalog endpoint. Only `banks` is read: the carrier lists' document-type
 * codes ("1"…"9") are not the vocabulary the Itaú file writer reads (`RUT`/`CI`, see
 * `src/pages/empresa/bank-account-fields.ts`), so the panel keeps its own two short lists.
 */
export const BankAccountOptionsSchema = z.object({
  banks: z.array(BankCatalogEntrySchema).default([])
});
